import React from "react";

/**
 * A versatile pagination button component that can render as a numbered button or navigation arrow.
 */
interface PaginationButtonProps {
  /** Button type - 'left'/'right' for arrows, 'text' for numbered buttons */
  type?: "left" | "right" | "text";
  /** Text content for numbered buttons */
  text?: string;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Whether the button is in active/selected state */
  active?: boolean;
  /** Click handler for the button */
  onClick?: () => void;
}

export const PaginationButton: React.FC<PaginationButtonProps> = ({
  type = "text",
  text,
  disabled = false,
  active = false,
  onClick,
}) => {
  const isArrow = type === "left" || type === "right";

  const baseStyles =
    "flex h-10 w-10 items-center justify-center rounded-md focus:outline-none";
  const arrowStyles = disabled
    ? "bg-blue text-white opacity-50"
    : "bg-blue text-white hover:bg-blue-dark";
  const textStyles = active
    ? "border-2 border-yellow text-yellow"
    : disabled
      ? "border border-gray-pagination text-[#1F3F5F] opacity-50"
      : "border border-gray-pagination text-[#1F3F5F] hover:bg-gray-100";

  const buttonStyles = `${baseStyles} ${isArrow ? arrowStyles : textStyles}`;

  const renderContent = () => {
    if (type === "left") {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          fill="currentColor"
          viewBox="0 0 256 256"
        >
          <path d="M168.49,199.51a12,12,0,0,1-17,17l-80-80a12,12,0,0,1,0-17l80-80a12,12,0,0,1,17,17L97,128Z"></path>
        </svg>
      );
    }
    if (type === "right") {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          fill="currentColor"
          viewBox="0 0 256 256"
        >
          <path d="M184.49,136.49l-80,80a12,12,0,0,1-17-17L159,128,87.51,56.49a12,12,0,0,1,17-17l80,80A12,12,0,0,1,184.49,136.49Z"></path>
        </svg>
      );
    }
    return text;
  };

  return (
    <button className={buttonStyles} onClick={onClick} disabled={disabled}>
      {renderContent()}
    </button>
  );
};
